import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { faPlay, faPlus } from '@fortawesome/pro-regular-svg-icons';

import { take } from 'rxjs';

import { Media } from '@eiq/files/models';

import { FileType, FileTypeTexts } from '@empathy/global/data';

import { LoopSourceMedia } from '@empathy/survey/base';

import { LoopStimuliDialogComponent } from './loop-stimuli-dialog/loop-stimuli-dialog.component';
import { IBlockForm } from '../../../models/blocks/block-form.interface';
import { LoopMediaCatalogue } from '../../../models/blocks/loop-media-catalogue.model';
import { LOOP_STIMULUS_TYPES } from '../../../models/blocks/loop-stimulus-types.constants';
import { SurveyEditorService } from '../../../services/survey-editor.service';
import { StimulusPickerDialogComponent } from '../../stimulus/stimulus-picker/stimulus-picker-dialog.component';

const THUMBNAIL_COUNT = 9;

@Component({ selector: 'empathy-loop-stimuli', templateUrl: 'loop-stimuli.component.html', styleUrls: ['./loop-stimuli.component.scss'] })
export class LoopStimuliComponent {
  block = input.required<FormGroup<IBlockForm>>();
  readonly media$ = signal<LoopSourceMedia[]>([]);
  // The shared renderer refreshes URLs in place; give it presentation copies.
  readonly thumbnails$ = computed(() =>
    this.media$()
      .slice(0, THUMBNAIL_COUNT)
      .map((media) => ({ media, preview: [{ ...media }], isVideo: media.type === FileType.VideoMp4 }))
  );
  readonly remainingCount$ = computed(() => Math.max(0, this.media$().length - THUMBNAIL_COUNT));
  readonly faPlus = faPlus;
  readonly faPlay = faPlay;
  readonly acceptedTypes = LOOP_STIMULUS_TYPES;
  readonly acceptedTypesText = LOOP_STIMULUS_TYPES.map((type) => FileTypeTexts[type]);
  readonly maxFileSize = 50;

  private readonly dialog = inject(MatDialog);
  private readonly editor = inject(SurveyEditorService);
  private pickerRef?: MatDialogRef<StimulusPickerDialogComponent, Media[]>;
  constructor() {
    effect(
      (onCleanup) => {
        const control = this.block().controls.sourceMedia;
        this.media$.set(control.value ?? []);
        const subscription = control.valueChanges.subscribe((media) => this.media$.set(media ?? []));
        onCleanup(() => subscription.unsubscribe());
      },
      { allowSignalWrites: true }
    );
  }

  onAdd(): void {
    const block = this.block();
    const dialogRef = this.dialog.open<StimulusPickerDialogComponent, unknown, Media[]>(StimulusPickerDialogComponent, {
      data: { multipleInd: true, types: LOOP_STIMULUS_TYPES }
    });
    this.pickerRef = dialogRef;
    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((media: Media[] | undefined) => {
        if (this.pickerRef === dialogRef) {
          this.pickerRef = undefined;
        }
        if (!media?.length) {
          return;
        }
        const catalogue = new LoopMediaCatalogue(block);
        catalogue.addMany(media);
        this.editor.surveyEditor$().blocks.refreshLists();
      });
  }

  onUpload(file: File): void {
    if (!this.pickerRef) {
      this.onAdd();
    }
    const dialogRef = this.pickerRef;
    // A dropped batch emits once per file; seed one picker after its view has initialized.
    dialogRef
      .afterOpened()
      .pipe(take(1))
      .subscribe(() => dialogRef.componentInstance.onUpload(file));
  }

  onViewAll(): void {
    this.dialog.open(LoopStimuliDialogComponent, {
      data: this.block(),
      panelClass: 'eiq-loop-stimuli-dialog',
      width: '1080px',
      height: '940px',
      maxWidth: 'calc(100vw - 48px)',
      maxHeight: 'calc(100dvh - 36px)'
    });
  }
}
